"use client";

import { useEffect, useRef } from "react";
import { useFormState, useFormStatus } from "react-dom";
import { CheckCircle2, Loader2, Send } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { submitPropertyInquiryAction } from "@/app/actions/inquiries";
import type { InquiryResult } from "@/lib/inquiries/service";
import { trackInquirySubmitted } from "@/lib/analytics/events";

const INITIAL_STATE: InquiryResult | null = null;

interface PropertyInquiryFormProps {
  propertySlug: string;
  propertyTitle: string;
  /**
   * Optional pre-filled message seeded from the "Pídelo" buttons on
   * the MissingInfoSection. The textarea uses this as its initial
   * value; the buyer can edit before sending. When the parent wants
   * to swap the seed, it must change the React `key` on this
   * component so the uncontrolled textarea re-initializes.
   */
  defaultMessage?: string;
  /** Pre-fill name from the authenticated buyer's profile, when known. */
  defaultName?: string;
  /** Pre-fill email from the authenticated buyer's session. */
  defaultEmail?: string;
}

export function PropertyInquiryForm({
  propertySlug,
  propertyTitle,
  defaultMessage,
  defaultName,
  defaultEmail,
}: PropertyInquiryFormProps) {
  const action = submitPropertyInquiryAction.bind(null, propertySlug);
  const [state, formAction] = useFormState<InquiryResult | null, FormData>(
    action,
    INITIAL_STATE
  );

  const fieldErrors =
    state?.status === "validation_error" ? state.fieldErrors : {};

  // Fire analytics once when the form transitions to success state.
  // Replaces the old waitlist Lead event — inquiry is a stronger
  // conversion signal (intent on a specific property, not generic
  // interest). Single fire guarded by a ref so re-renders don't
  // re-emit.
  const trackedRef = useRef(false);
  useEffect(() => {
    if (state?.status === "success" && !trackedRef.current) {
      trackedRef.current = true;
      trackInquirySubmitted({ propertySlug });
    }
  }, [state, propertySlug]);

  if (state?.status === "success") {
    return (
      <aside className="my-12 rounded-3xl bg-emerald/10 p-7 ring-1 ring-emerald/30 sm:p-9">
        <div className="flex items-start gap-3">
          <CheckCircle2
            className="mt-1 h-6 w-6 shrink-0 text-emerald"
            aria-hidden
          />
          <div>
            <p className="text-lg font-semibold text-ink">
              Recibimos tu consulta.
            </p>
            <p className="mt-1 text-sm leading-relaxed text-ink-700">
              Te escribimos en menos de 24 horas con los siguientes
              pasos: documentación de la propiedad, opciones de
              financiamiento que encajan con tu perfil, y conexión con
              tu abogado independiente. Sin compromiso.
            </p>
          </div>
        </div>
      </aside>
    );
  }

  return (
    <aside
      aria-label="Consulta sobre esta propiedad"
      className="my-12 rounded-3xl bg-cream-100 p-7 ring-1 ring-ink-200 sm:p-9"
    >
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-coral">
        ¿Te interesa esta propiedad?
      </p>
      <h2 className="mt-2 text-2xl font-bold leading-tight text-ink sm:text-3xl">
        Pregúntanos lo que quieras saber
      </h2>
      <p className="mt-3 text-sm text-ink-700 sm:text-base">
        Te respondemos en menos de 24 horas con la documentación de la
        propiedad, las opciones de financiamiento que encajan con tu
        perfil, y conexión con tu abogado independiente. Sin
        compromiso, sin costo.
      </p>

      <form action={formAction} className="mt-6 space-y-4" noValidate>
        {/* Honeypot */}
        <div
          aria-hidden
          className="pointer-events-none absolute left-[-9999px] h-0 w-0 overflow-hidden opacity-0"
        >
          <label htmlFor="hp-website-inq">Website (no completar)</label>
          <input
            id="hp-website-inq"
            name="website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            defaultValue=""
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field htmlFor="inq-name" label="Nombre" error={fieldErrors.name}>
            <Input
              id="inq-name"
              name="name"
              type="text"
              required
              autoComplete="given-name"
              placeholder="Tu nombre"
              defaultValue={defaultName ?? ""}
            />
          </Field>
          <Field htmlFor="inq-email" label="Email" error={fieldErrors.email}>
            <Input
              id="inq-email"
              name="email"
              type="email"
              required
              autoComplete="email"
              placeholder="tu@email.com"
              defaultValue={defaultEmail ?? ""}
            />
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            htmlFor="inq-phone"
            label="Teléfono"
            hint="Opcional"
            error={fieldErrors.phone}
          >
            <Input
              id="inq-phone"
              name="phone"
              type="tel"
              autoComplete="tel"
              placeholder="+1 ..."
            />
          </Field>
          <Field
            htmlFor="inq-budget"
            label="Tu presupuesto"
            hint="Opcional"
            error={fieldErrors.budgetRange}
          >
            <Input
              id="inq-budget"
              name="budgetRange"
              type="text"
              placeholder="ej. US$15K ahorrado, $1K/mes"
            />
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            htmlFor="inq-cedula"
            label="¿Tienes cédula dominicana?"
            hint="Define cómo financiamos"
            error={fieldErrors.hasCedula}
          >
            <Select id="inq-cedula" name="hasCedula" defaultValue="">
              <option value="">— Selecciona —</option>
              <option value="yes">Sí, activa</option>
              <option value="no">No</option>
              <option value="not_sure">No estoy seguro / la tengo pero no sé si está vigente</option>
            </Select>
          </Field>
          <Field
            htmlFor="inq-financing"
            label="Cómo piensas financiar"
            error={fieldErrors.financingPath}
          >
            <Select id="inq-financing" name="financingPath" defaultValue="">
              <option value="">— Selecciona —</option>
              <option value="cash">Cash (sin financiar)</option>
              <option value="us_loan">Préstamo en EEUU (HELOC, etc.)</option>
              <option value="dr_mortgage">Hipoteca dominicana</option>
              <option value="undecided">Aún no decido</option>
            </Select>
          </Field>
        </div>

        <Field
          htmlFor="inq-time"
          label="Mejor hora para llamarte"
          hint="Opcional · zona horaria EEUU"
          error={fieldErrors.bestTimeToCall}
        >
          <Input
            id="inq-time"
            name="bestTimeToCall"
            type="text"
            placeholder="ej. tardes EST, fines de semana"
          />
        </Field>

        <Field
          htmlFor="inq-message"
          label="¿Algo específico que quieras saber?"
          hint="Opcional"
          error={fieldErrors.message}
        >
          <textarea
            id="inq-message"
            name="message"
            rows={4}
            maxLength={2000}
            className="block w-full rounded-xl border border-ink-200 bg-white px-3.5 py-2.5 text-base text-ink shadow-sm transition focus:border-coral focus:outline-none focus:ring-2 focus:ring-coral/30"
            placeholder={`Sobre "${propertyTitle}"...`}
            defaultValue={defaultMessage}
          />
        </Field>

        {state?.status === "error" && (
          <p role="alert" className="text-sm text-coral">
            {state.message}
          </p>
        )}

        <SubmitButton />

        <p className="text-xs leading-relaxed text-ink-500">
          Tu información se usa solo para responderte sobre esta
          propiedad. No spam, no se comparte con terceros.
        </p>
      </form>
    </aside>
  );
}

function SubmitButton() {
  const { pending } = useFormStatus();
  return (
    <Button
      type="submit"
      variant="primary"
      size="lg"
      disabled={pending}
      className="w-full sm:w-auto"
    >
      {pending ? (
        <>
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
          Enviando…
        </>
      ) : (
        <>
          <Send className="h-5 w-5" aria-hidden />
          Enviar consulta
        </>
      )}
    </Button>
  );
}
